import { FIRST_PAGE } from "constants/ContactConstant";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "react-query";
import { message } from "antd";
import MembershipsService from "services/MembershipsService";
import unpackAndShowErrors from "helpers/djangoErrorUnapacker";

export const GET_MEMBERSHIPS = "GET_MEMBERSHIPS";
export const GET_LOYALTY_MEMBERSHIP = "GET_LOYALTY_MEMBERSHIP";
export const GET_CONTACT_CARDS = "GET_CONTACT_CARDS";
export const GET_SUBSCRIPTIONS = "GET_SUBSCRIPTIONS";
export const GET_LOYALTY_SUBSCRIPTIONS = "GET_LOYALTY_SUBSCRIPTIONS";
export const GET_LOYALTY_OFFER_MEMBERSHIPS = "GET_LOYALTY_OFFER_MEMBERSHIPS";
export const GET_LOYALTY_INVOICES = "GET_LOYALTY_INVOICES";
export const LIST_CONTACT_CARDS = "LIST_CONTACT_CARDS";
export const LIST_AVAILABLE_CARDS = "LIST_AVAILABLE_CARDS";

const useCreateMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.createMembership(payload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const useGetMemberships = (payload) =>
  useQuery({
    queryKey: [GET_MEMBERSHIPS, payload],
    queryFn: () => MembershipsService.getMemberships(payload),
  });

const useGetLoyaltyOfferMemberships = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_LOYALTY_OFFER_MEMBERSHIPS, payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      MembershipsService.getLoyaltyOfferMemberships({
        ...payload,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.next) {
        return lastPage.data.current + 1;
      }
      return undefined;
    },
    initialPageParam: FIRST_PAGE,
  });

const useGetLoyaltyMembership = (membershipId) =>
  useQuery({
    queryKey: [GET_LOYALTY_MEMBERSHIP, membershipId],
    queryFn: () => MembershipsService.getLoyaltyMembership(membershipId),
    enabled: !!membershipId,
  });

const useDisableMembership = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId) => {
      return MembershipsService.disableMembership(membershipId);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const usePatchMembership = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => {
      return MembershipsService.patchMembership(data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const usePatchVariation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => {
      return MembershipsService.patchVariation(data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const useCreateVariation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.createVariation(payload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const useDisableVariation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.disableVariation(payload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GET_MEMBERSHIPS, { page: FIRST_PAGE });
    },
    onError: ({ response: { data } }) => {
      console.log(data);
    },
  });
};

const useCreateSubscription = (successMessage, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.createSubscription(payload);
    },
    onSuccess: () => {
      message.success(successMessage);
      queryClient.invalidateQueries(GET_MEMBERSHIPS);
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useCancelContactMembership = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId) => {
      return MembershipsService.cancelContactMembership(membershipId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_SUBSCRIPTIONS);
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useGetContactCards = (payload, enabled = true) => {
  return useQuery({
    queryKey: [GET_CONTACT_CARDS, payload],
    queryFn: () => MembershipsService.getContactCards(payload),
    enabled: enabled,
  });
};

const useGetSubscriptions = (payload) =>
  useQuery({
    queryKey: [GET_SUBSCRIPTIONS, payload],
    queryFn: () => MembershipsService.getSubscriptions(payload),
  });

const useGetLoyaltySubscriptions = (payload) =>
  useInfiniteQuery({
    queryKey: [GET_LOYALTY_SUBSCRIPTIONS, payload],
    queryFn: ({ pageParam = FIRST_PAGE }) =>
      MembershipsService.getSubscriptions({ ...payload, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.next) {
        return lastPage.data.current + 1;
      }
      return undefined;
    },
    initialPageParam: FIRST_PAGE,
  });

const useGetLoyaltyInvoices = (payload) =>
  useQuery({
    queryKey: [GET_LOYALTY_INVOICES, payload],
    queryFn: () => MembershipsService.getInvoices(payload),
    enabled: !!payload?.contact_id,
  });

const useCancelSubscription = (successMessage, onSuccess) => {
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.cancelSubscription(payload);
    },
    onSuccess: () => {
      message.success(successMessage);
      onSuccess();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useListContactCards = (payload) => {
  return useQuery({
    queryKey: [LIST_CONTACT_CARDS, payload],
    queryFn: () => MembershipsService.listContactCards(payload),
    enabled: !!payload?.contact_id,
  });
};

const useListActiveContactCards = (payload) => {
  return useQuery({
    queryKey: [LIST_CONTACT_CARDS, "active", payload],
    queryFn: () => MembershipsService.listActiveContactCards(payload),
    enabled: !!payload?.contact_id,
  });
};

const useCreateCard = (successMessage, onSuccess, onError) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.createCard(payload);
    },
    onSuccess: () => {
      message.success(successMessage);
      queryClient.invalidateQueries(LIST_CONTACT_CARDS);
      onSuccess?.();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
      onError?.();
    },
  });
};

const useDeleteCard = (successMessage) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cardId) => {
      return MembershipsService.deleteCard(cardId);
    },
    onSuccess: () => {
      message.success(successMessage);
      queryClient.invalidateQueries(LIST_CONTACT_CARDS);
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useChangeMembershipCard = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return MembershipsService.changeMembershipCard(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_SUBSCRIPTIONS);
      queryClient.invalidateQueries(GET_LOYALTY_SUBSCRIPTIONS);
      onSuccess();
    },
    onError: ({ response: { data } }) => {
      unpackAndShowErrors(data);
    },
  });
};

const useListAvailableCards = (membershipId) => {
  return useQuery({
    queryKey: [LIST_AVAILABLE_CARDS, membershipId],
    queryFn: () => MembershipsService.listAvailableCards(membershipId),
  });
};

export {
  useGetMemberships,
  usePatchVariation,
  useGetContactCards,
  useCreateVariation,
  usePatchMembership,
  useDisableVariation,
  useCreateMembership,
  useGetSubscriptions,
  useDisableMembership,
  useCancelSubscription,
  useCancelContactMembership,
  useCreateSubscription,
  useGetLoyaltyInvoices,
  useGetLoyaltyOfferMemberships,
  useGetLoyaltyMembership,
  useGetLoyaltySubscriptions,
  useListContactCards,
  useListActiveContactCards,
  useCreateCard,
  useDeleteCard,
  useChangeMembershipCard,
  useListAvailableCards,
};
